import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateApplication } from 'app/frontend/base/api/application'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import ApplicationInfoBlock from './ApplicationInfoBlock'
import ApplicationCharacterCard from 'app/frontend/base/components/ApplicationCharacterCard'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  applicationStatusColors,
  applicationStatusLabels,
} from 'app/frontend/types/custom/ApplicationStatus'
import { cn } from '@/lib/utils'
import {
  useApplicationDetails,
  useCharacterDetails,
} from 'app/frontend/base/config/queries'
import ApplicationAnswers from './ApplicationAnswers'
import { defaultLegacyFormColors, urlToMainSite } from '../utils'
import JoinDiscordButton from './JoinDiscordButton'
import ApplicationMessagesDialog from './ApplicationMessagesDialog'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import TeamRecruitmentDetails from 'app/frontend/types/custom/TeamRecruitmentDetails'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import LinkButton from '@/components/ui/link-button'

dayjs.extend(RelativeTime)

type Props = {
  application: wowaudit.Application
  inLegacyForm?: boolean
  inUserProfile?: boolean
  teamReference?: wowaudit.TeamReference
  recruitmentDetails?: TeamRecruitmentDetails
}

const UserApplicationDetails = ({
  application,
  inLegacyForm = false,
  inUserProfile = false,
  teamReference,
  recruitmentDetails,
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)
  const { refetch, data: applicationDetails } = useApplicationDetails(
    teamReference?.id,
    application.id,
  )
  const { data: characterDetails } = useCharacterDetails(
    application.characterReference?.id,
  )

  const { mutate: withdrawApplication } = useMutation({
    mutationFn: (status: 'withdrawn' | 'submitted') =>
      updateApplication<wowaudit.UserApplication>(
        teamReference?.id,
        application.id,
        { status },
        'user',
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userApplications'] })
      queryClient.invalidateQueries({
        queryKey: ['applicationDetails', { id: application.id }],
      })
      setWithdrawDialogOpen(false)
      toast.success(`Application status changed successfully`)
    },
    onError: (error) => displayErrorToast(error, t),
  })

  const canWithdraw = ['submitted', 'under_review', 'accepted'].includes(
    application.status,
  )

  const actions = (
    <div className='flex justify-end gap-3 pt-2 flex-wrap max-[410px]:*:w-full'>
      {recruitmentDetails?.discord_link && (
        <JoinDiscordButton
          to={recruitmentDetails?.discord_link}
          className={inLegacyForm ? 'text-accent-foreground' : ''}
        />
      )}

      <ApplicationMessagesDialog
        type='messages'
        applicationIdProp={application.id}
      />

      {['submitted', 'under_review'].includes(application.status) ? (
        <LinkButton
          to={`${application.teamReference?.path}/apply/${application.id}`}
        >
          Edit
        </LinkButton>
      ) : null}

      <Button
        variant='outline'
        disabled={!canWithdraw && application.status !== 'withdrawn'}
        onClick={() => setWithdrawDialogOpen(true)}
      >
        {canWithdraw ? 'Withdraw' : 'Resubmit'}
      </Button>
    </div>
  )

  return (
    <div className='space-y-4'>
      <ConfirmDialog
        open={withdrawDialogOpen}
        descriptionKey={
          canWithdraw
            ? 'forms.withdraw-application-warning'
            : 'forms.resubmit-application-warning'
        }
        onCancel={() => setWithdrawDialogOpen(false)}
        onContinue={() =>
          canWithdraw
            ? withdrawApplication('withdrawn')
            : withdrawApplication('submitted')
        }
      />

      {inLegacyForm ? (
        <>
          <p>{recruitmentDetails?.submitted_introduction}</p>
          <div
            className='flex justify-between items-center gap-2 rounded-md py-3 px-4 text-lg'
            style={{
              background:
                recruitmentDetails?.css_inputs ??
                defaultLegacyFormColors.css_inputs,
            }}
          >
            <span>Status</span>
            <span className={applicationStatusColors[application.status].text}>
              {applicationStatusLabels[application.status]}
            </span>
          </div>
        </>
      ) : (
        <>
          <h2 className='flex justify-between items-center flex-wrap gap-2'>
            <span className='flex items-center gap-4'>
              <span>Application details</span>{' '}
              {inUserProfile ? (
                <Link
                  to={`/profile/applications`}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'custom' }),
                    'h-7 px-3',
                  )}
                >
                  Back
                </Link>
              ) : null}
            </span>

            <div className='max-md:hidden'>{actions}</div>
          </h2>

          {recruitmentDetails?.submitted_introduction &&
          ['submitted', 'under_review'].includes(application.status) ? (
            <Alert>
              <AlertDescription>
                <strong>Message from team:</strong>{' '}
                {recruitmentDetails?.submitted_introduction}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <ApplicationInfoBlock title='Submitted'>
              <Tooltip>
                <TooltipTrigger className='text-left'>
                  {dayjs(application.createdAt).fromNow()}
                </TooltipTrigger>
                <TooltipContent>
                  {dayjs(application.createdAt).format('D MMM YYYY, HH:mm')}
                </TooltipContent>
              </Tooltip>
            </ApplicationInfoBlock>

            <ApplicationInfoBlock
              title='Status'
              className={cn(applicationStatusColors[application.status].text)}
            >
              <span>{applicationStatusLabels[application.status]}</span>
            </ApplicationInfoBlock>

            <ApplicationInfoBlock
              title='Team'
              className='flex items-center gap-2'
            >
              <BannerImage
                faction={teamReference?.ownerReference.faction}
                fileName={teamReference?.ownerReference.bannerFilename}
                size='custom'
                className='w-6 h-6 min-w-6 min-h-6'
              />
              <Link
                to={urlToMainSite(teamReference?.path ?? '#')}
                className='truncate'
              >
                {teamReference?.ownerReference.name} - {teamReference?.name}
              </Link>
            </ApplicationInfoBlock>
          </div>

          <ApplicationCharacterCard
            character={application.characterReference}
            role={application.role}
            characterDetails={characterDetails?.[0]}
            legacyCharacterPath={application.legacyCharacterPath}
            legacyCharacterName={application.legacyCharacterName}
            legacyCharacterClass={application.legacyCharacterClass}
            legacyCharacterRealm={application.legacyCharacterRealm}
            onRefresh={refetch}
          />

          <ApplicationAnswers answers={applicationDetails?.answers ?? []} />
        </>
      )}

      {actions}
    </div>
  )
}

export default UserApplicationDetails
